import { useMemo } from "react";

import { Locale } from "@/Common/Models/Localization/Locale";
import { ResourceProvider } from "@/Common/Resources/StaticResources";
import { cacheStore } from "@/Globals/Stores/CacheStore";

/**
 * Provides access to translation text from the underlying resource provider or cache store.
 */
interface ITranslationHook {
    /**
     * Get the text for a translation key.
     * @param key The unique translation key.
     * @param tokens An object containing token values to substitute in the string.
     * @param locale The locale code for which to get translations, or null for the current locale. This must already be loaded via {@link cacheStore.RequestApplicationStringAsync}, or be a predefined locale when using a static resource provider.
     */
    getText(key: string, tokens?: object, locale?: Locale): string;
}

/**
 * Get a hook which provides access to translation text.
 * @param resourceProvider The resource provider from which to read translations, or null to use the cache store.
 */
function useTranslation(resourceProvider?: ResourceProvider): ITranslationHook {
    return useMemo(
        () => ({
            getText: resourceProvider
                ? (key, tokens, locale) => resourceProvider.GetString(key, tokens, locale)
                : (key, tokens, locale) => cacheStore.GetString(key, tokens, locale)
        }),
        [resourceProvider]
    );
}

export { ITranslationHook, useTranslation };
